import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@mui/material";
import { Button } from "react-bootstrap";
import Navbar from "../../layout/Navbar/Navbar";
import Sidebar from "../../layout/Sidebar/SideBar";
import blogFetch from "../../axios/config";

interface PrioridadeItem {
    prioridade_id: number;
    prioridade_pedido: string;
    prioridade_zfer: string;
    prioridade_observacao?: string;
    _ts_Creat: string;
    _Creat?: string;
}

export default function PrioridadePCP() {
    const [pedido, setPedido] = useState("");
    const [zfer, setZfer] = useState("");
    const [obs, setObs] = useState("");
    const [lista, setLista] = useState<PrioridadeItem[]>([]);
    const [filtroPedido, setFiltroPedido] = useState("");

    // Novo estado para modo de lista
    const [modoLista, setModoLista] = useState(false);
    const [listaItens, setListaItens] = useState("");

    const [pedidoValido, setPedidoValido] = useState<boolean | null>(null);
    const [zferValido, setZferValido] = useState<boolean | null>(null);

    const user = useMemo(
        () => JSON.parse(localStorage.getItem("user") || "{}"),
        []
    );

    const carregarPriorizacoes = useCallback(async () => {
        const { data } = await blogFetch.get("ISAGA_PriorizacaoPCP/priorizacoes");
        setLista(data);
    }, []);

    const podeAdicionar =
        (pedido && pedidoValido === true) ||
        (zfer && zferValido === true);

    useEffect(() => {
        carregarPriorizacoes();
    }, [carregarPriorizacoes]);

    // Função para processar a lista de itens
    const processarLista = (texto: string): string[] => {
        // Remove espaços e quebras de linha extras e divide por linha
        return texto
            .split(/[\n,;\s]+/)
            .map(item => item.trim())
            .filter(item => item.length > 0);
    };

    // Contar quantos itens válidos tem na lista
    const contarItensLista = useMemo(() => {
        if (!modoLista || !listaItens.trim()) return 0;
        return processarLista(listaItens).length;
    }, [modoLista, listaItens]);

    const adicionar = async () => {
        await blogFetch.post("ISAGA_PriorizacaoPCP/priorizar", {
            prioridade_pedido: pedido || null,
            prioridade_zfer: zfer || null,
            prioridade_observacao: obs || null,
            _ts_Creat: new Date().toISOString(),
            _Creat: user.user_UserName
        });

        setPedido("");
        setZfer("");
        setObs("");
        setPedidoValido(null);
        setZferValido(null);

        carregarPriorizacoes();
    };

    const adicionarLista = async () => {
        const itens = processarLista(listaItens);

        if (itens.length === 0) {
            alert("Nenhum item válido encontrado na lista");
            return;
        }

        const confirmar = window.confirm(
            `Você está prestes a adicionar ${itens.length} item(ns) à priorização. Deseja continuar?`
        );

        if (!confirmar) return;

        // Adiciona cada item individualmente
        for (const item of itens) {
            await blogFetch.post("ISAGA_PriorizacaoPCP/priorizar", {
                prioridade_pedido: item || null,
                prioridade_zfer: null,
                prioridade_observacao: obs || null,
                _ts_Creat: new Date().toISOString(),
                _Creat: user.user_UserName
            });
        }

        // Limpa os campos
        setListaItens("");
        setObs("");

        // Recarrega a lista
        carregarPriorizacoes();

        alert(`${itens.length} item(ns) adicionado(s) com sucesso!`);
    };

    const limparTudo = async () => {
        const confirmar = window.confirm(
            "Tem certeza que deseja remover TODAS as priorizações?"
        );

        if (!confirmar) return;

        await blogFetch.delete("ISAGA_PriorizacaoPCP/limpar");

        setLista([]);
    };

    const remover = async (id: number) => {
        await blogFetch.delete(`ISAGA_PriorizacaoPCP/remover/${id}`);
        setLista(prev => prev.filter(x => x.prioridade_id !== id));
    };

    const listaFiltrada = useMemo(
        () =>
            lista.filter(x => {
                const filtro = filtroPedido.toLowerCase();

                return (
                    (x.prioridade_pedido ?? "")
                        .toLowerCase()
                        .includes(filtro) ||
                    (x.prioridade_zfer ?? "")
                        .toLowerCase()
                        .includes(filtro)
                );
            }),
        [lista, filtroPedido]
    );

    return (
        <>
            <Navbar />
            <Sidebar />

            <div className="app_body">
                <Card className="mb-4 shadow-sm rounded-2xl">
                    <CardContent className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">Priorização PCP</h4>

                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn ${!modoLista ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setModoLista(false)}
                                >
                                    Item Individual
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${modoLista ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setModoLista(true)}
                                >
                                    Lista de Itens
                                </button>
                            </div>
                        </div>

                        {!modoLista ? (
                            // Modo individual (original)
                            <>
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label">Pedido</label>
                                        <input
                                            className="form-control"
                                            value={pedido}
                                            onChange={e => setPedido(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">ZFER</label>
                                        <input
                                            className="form-control"
                                            value={zfer}
                                            onChange={e => setZfer(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Observação</label>
                                        <input
                                            className="form-control"
                                            value={obs}
                                            onChange={e => setObs(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end mt-3">
                                    <Button onClick={adicionar}>
                                        Priorizar
                                    </Button>
                                </div>
                            </>
                        ) : (
                            // Modo lista (novo)
                            <>
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Lista de Pedidos
                                            {contarItensLista > 0 && (
                                                <span className="badge bg-info ms-2">
                                                    {contarItensLista} item(ns) detectado(s)
                                                </span>
                                            )}
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows={8}
                                            value={listaItens}
                                            onChange={e => setListaItens(e.target.value)}
                                            placeholder="Cole aqui a lista de pedidos (um por linha ou separados por espaço)"
                                        />
                                        <small className="text-muted">
                                            Cole sua lista de pedidos. Pode ser separado por linha, espaço, vírgula ou ponto e vírgula.
                                        </small>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label">Observação (aplicada a todos os itens)</label>
                                        <input
                                            className="form-control"
                                            value={obs}
                                            onChange={e => setObs(e.target.value)}
                                            placeholder="Observação que será aplicada a todos os pedidos da lista"
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end mt-3">
                                    <Button
                                        onClick={adicionarLista}
                                        disabled={contarItensLista === 0}
                                    >
                                        Priorizar {contarItensLista > 0 && `(${contarItensLista} itens)`}
                                    </Button>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                <Card className="shadow-sm rounded-2xl">
                    <CardContent className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Pedidos Priorizados</h5>

                            <div className="d-flex gap-2">
                                <input
                                    className="form-control"
                                    style={{ width: 250 }}
                                    placeholder="Filtrar por pedido"
                                    value={filtroPedido}
                                    onChange={e => setFiltroPedido(e.target.value)}
                                />

                                <Button
                                    variant="outline-danger"
                                    onClick={limparTudo}
                                    disabled={lista.length === 0}
                                >
                                    Limpar Lista
                                </Button>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Pedido</th>
                                        <th>ZFER</th>
                                        <th>Observação</th>
                                        <th>Data</th>
                                        <th>Priorizado Por</th>
                                        <th className="text-center">Remover</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listaFiltrada.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="text-center text-muted">
                                                Nenhum pedido encontrado
                                            </td>
                                        </tr>
                                    )}

                                    {listaFiltrada.map(item => (
                                        <tr key={item.prioridade_id}>
                                            <td>{item.prioridade_pedido}</td>
                                            <td>{item.prioridade_zfer}</td>
                                            <td>{item.prioridade_observacao}</td>
                                            <td>{item._ts_Creat}</td>
                                            <td>{item._Creat}</td>
                                            <td className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => remover(item.prioridade_id)}
                                                >
                                                    Remover
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}